import React, {useEffect, useState, useRef} from 'react';
import {Form, Input, Select, TimePicker} from 'antd';
import { passwordPolicyRule } from 'utils/passwordValidator';
import EditableDrawer from 'components/EditableDrawer';
import {FormInstance} from 'antd/es/form';
import {CountryCodeAndPhoneNumber, PhoneInput} from 'components/PhoneInput';
import {useWarehouseOptions} from 'hooks/useWarehouseOptions';
import {useDspOptions} from 'hooks/useDspOptions';
import {SiteType} from 'graphql/types/SiteType';
import {ROLE_OPTIONS} from 'constants/roleOptions';
import {useWarehousePickupZones} from 'hooks/useWarehousePickupZones';
import {useVehicleOptions} from 'hooks/useVehicleOptions';
import dayjs from 'dayjs';
import {AccountFormData} from 'hooks/useAccountApis';
import { useTranslation } from 'react-i18next';
import {getUserInfo, isHeadquartersAdmin} from '../../../../utils/auth';
import {useQuery} from "@apollo/client";
import {GET_ALL_TMS_CARRIER_BY_WAREHOUSEID} from "../../../../graphql/queries/getTmsCarriers";
import {GET_VEHICLES, VehiclesResponse} from "../../../tms/vehicle/graphql/queries/getVehicles";
import {TmsVehicleType} from "../../../../graphql/types/TmsVehicleType";

// Create a new options list without the 'all' option for the form
// Filter CS_FOLLOW_UP role: only HEADQUARTERS_ADMIN can see it
const getRoleFormOptions = () => {
    const baseOptions = ROLE_OPTIONS.filter(option => option.value !== 'all');
    // Only HEADQUARTERS_ADMIN can create CS_FOLLOW_UP role
    if (!isHeadquartersAdmin()) {
        return baseOptions.filter(option => option.value !== 'CS_FOLLOW_UP');
    }
    return baseOptions;
};

interface AccountFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: AccountFormData) => Promise<void>;
    account?: AccountFormData | null; // If account data is provided, it's edit mode, otherwise it's create mode
    mode: 'create' | 'edit'; // Explicitly specify mode
}

const AccountFormDrawer: React.FC<AccountFormDrawerProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onSubmit,
                                                                 account,
                                                                 mode
                                                             }) => {
    const {t} = useTranslation();
    const drawerTitle = mode === 'create' ? t('central.personnel.pageDetails.new') : t('central.personnel.pageDetails.personnelDetails');
    const [form] = Form.useForm();

    useEffect(() => {
        if (account) {
            form.resetFields();
            // 确保 responsibleZipcodes 字段总是有值
            const accountWithDefaults = {
                ...account,
                responsibleZipcodes: account.responsibleZipcodes || [],
                // 将字符串时间转换为 Dayjs 时间范围
                workHours: account.workStartTime && account.workEndTime ? 
                    [dayjs(account.workStartTime, 'HH:mm'), dayjs(account.workEndTime, 'HH:mm')] : 
                    [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]
            };
            form.setFieldsValue(accountWithDefaults);
        } else {
            form.resetFields();
            form.setFieldsValue({
                status: 1,
                isOnDuty: true,
                phone: {
                    countryCode: 1,
                    phoneNumber: '',
                },
                responsibleZipcodes: [],
                workHours: [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')],
            });
        }
    }, [account, form, visible]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();
            
            // 将时间范围转换为独立的开始和结束时间
            const processedValues = {
                ...values,
                workStartTime: values.workHours && values.workHours[0] ? 
                    values.workHours[0].format('HH:mm') : '09:00',
                workEndTime: values.workHours && values.workHours[1] ? 
                    values.workHours[1].format('HH:mm') : '18:00'
            };
            
            // 调试日志：查看表单收集的原始数据
            console.log('Form values collected:', processedValues);
            console.log('Responsible zipcodes from form:', processedValues.responsibleZipcodes);
            console.log('Work hours:', values.workHours, '-> Start:', processedValues.workStartTime, 'End:', processedValues.workEndTime);
            
            await onSubmit(processedValues);
        } catch (error) {
            console.error('error', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting ? t('components.button.processing') as string : t('components.button.confirm') as string}
            cancelText={t('components.button.cancel') as string}
            confirmDisabled={isSubmitting}
            width={600}
        >
            <AccountForm
                form={form}
                mode={mode}
            />
        </EditableDrawer>
    );
};

const STATUS_OPTIONS = [
    {value: 1, labelEn: 'Active', labelEs: 'Activo', labelZh: '启用'},
    {value: 0, labelEn: 'Inactive', labelEs: 'Inactivo', labelZh: '禁用'}
];

const DUTY_STATUS_OPTIONS = [
    {value: true, labelEn: 'On Duty', labelEs: 'En Servicio', labelZh: '在职'},
    {value: false, labelEn: 'Off Duty', labelEs: 'Fuera de Servicio', labelZh: '离职'}
];


interface AccountFormProps {
    mode: 'create' | 'edit';
    form: FormInstance<AccountFormData>;
}

const AccountForm = ({mode, form}: AccountFormProps) => {
    const {t, i18n} = useTranslation();

    const getOptionLabel = (option: any) => {
        switch(i18n.language) {
            case 'zh':
            case 'zh-CN':
                return option.labelZh;
            case 'es':
                return option.labelEs;
            case 'en':
            default:
                return option.labelEn;
        }
    };

    const statusOptions = STATUS_OPTIONS.map(option => ({
        value: option.value,
        label: getOptionLabel(option)
    }));

    const dutyStatusOptions = DUTY_STATUS_OPTIONS.map(option => ({
        value: option.value,
        label: getOptionLabel(option)
    }));

    const onFinish = (values: any) => {
        console.log(values);
    }

    const role = Form.useWatch('role', form);

    // 当角色发生变化时，清除相关字段的值（仅在创建模式下）
    useEffect(() => {
        if (role && mode === 'create') {
            // 清除之前角色相关的字段值
            form.setFieldsValue({
                dspId: undefined,
                warehouseId: undefined,
                isOnDuty: undefined,
                vehicleTypeId: undefined,
                licensePlate: undefined,
                vehiclePhotos: undefined,
                responsibleZipcodes: [],
                workWeekdays: [],
                workHours: [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')],
                vehicleId: undefined
            });
        }
    }, [role, mode, form]);

    const DspManagerOptions = () => {
        const {dspOptions, loading} = useDspOptions();
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.company')} name="dspId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectCompany') as string}]}>
                    <Select
                        loading={loading}
                        options={dspOptions}
                        placeholder={t('central.personnel.placeholders.select') as string}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>
            </>
        )
    };

    const HubManagerOptions = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };

    const WarehouseManagerOptions = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.WAREHOUSE);
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.warehouse')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };

    const WarehouseOperatorOptions = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.WAREHOUSE);
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.warehouse')} name="warehouseId"
                           rules={[{required: true, message: t('central.personnel.messages.pleaseSelectWarehouse') as string}]}>
                    <Select
                        loading={loading}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };

    const HubOperatorOptions = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };

    const PickupDispatcherOptions = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);
        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };

    const PickupDriverOptions = () => {
        const {t} = useTranslation();
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);
        
        // 监听Hub选择变化
        const warehouseId = Form.useWatch('warehouseId', form);
        const {pickupZones, loading: pickupZonesLoading} = useWarehousePickupZones(warehouseId);
        const {vehicleOptions, loading: vehicleOptionsLoading} = useVehicleOptions(warehouseId);
        
        // 工作日选项
        const weekdayOptions = [
            { value: 1, label: t('enum.weekdaysOptions.monday')  },
            { value: 2, label: t('enum.weekdaysOptions.tuesday') },
            { value: 3, label: t('enum.weekdaysOptions.wednesday') },
            { value: 4, label: t('enum.weekdaysOptions.thursday') },
            { value: 5, label: t('enum.weekdaysOptions.friday') },
            { value: 6, label: t('enum.weekdaysOptions.saturday') },
            { value: 7, label: t('enum.weekdaysOptions.sunday') }
        ];

        // 将pickup zones转换为选项格式
        const pickupZoneOptions = pickupZones.map(zipcode => ({
            value: zipcode,
            label: zipcode
        }));

        // 当Hub选择变化时，清空已选择的Pickup Range和Vehicle
        const prevWarehouseIdRef = useRef(warehouseId);
        useEffect(() => {
            // 只有当Hub真正发生变化时才清空字段（不是初始化时）
            if (prevWarehouseIdRef.current !== warehouseId && prevWarehouseIdRef.current !== undefined) {
                form.setFieldsValue({ 
                    responsibleZipcodes: [],
                    vehicleId: undefined 
                });
            }
            prevWarehouseIdRef.current = warehouseId;
        }, [warehouseId]);

        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
                <Form.Item label={t('central.personnel.pageDetails.pickupRange')} name="responsibleZipcodes" initialValue={[]}>
                    <Select
                        mode="multiple"
                        loading={pickupZonesLoading}
                        placeholder={warehouseId ? t('central.personnel.placeholders.selectFromAvailablePickupZones') as string : t('central.personnel.placeholders.pleaseSelectHubFirst') as string}
                        disabled={!warehouseId || pickupZones.length === 0}
                        options={pickupZoneOptions}
                        style={{ width: '100%' }}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>  
                <Form.Item 
                    label={t('central.personnel.pageDetails.workWeekdays')}
                    name="workWeekdays" 
                    initialValue={[1,2,3,4,5]}
                    rules={[{required: true, message: t('central.personnel.messages.pleaseSelectWorkWeekdays') as string}]}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('central.personnel.placeholders.selectWorkDays') as string}
                        options={weekdayOptions}
                    />
                </Form.Item>
                <Form.Item 
                    label={t('central.personnel.pageDetails.workHours')}
                    name="workHours" 
                    initialValue={[dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]}
                    rules={[{required: true, message: t('central.personnel.messages.pleaseSetWorkHours') as string}]}
                >
                    <TimePicker.RangePicker 
                        format="HH:mm"
                        placeholder={[t('central.personnel.placeholders.startTime') as string, t('central.personnel.placeholders.endTime') as string]}
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                <Form.Item label={t('central.personnel.pageDetails.vehicle')} name="vehicleId">
                    <Select
                        loading={vehicleOptionsLoading}
                        placeholder={warehouseId ? t('central.personnel.placeholders.selectVehicle') as string : t('central.personnel.placeholders.pleaseSelectHubFirst') as string}
                        disabled={!warehouseId || vehicleOptions.length === 0}
                        allowClear
                        options={vehicleOptions}
                    />
                </Form.Item>
            </>
        )
    };
    interface TmsDispatcherOptionsProps {
        form: FormInstance<AccountFormData>;
        mode: 'create' | 'edit'; // 添加 mode 参数
    }

        const TmsDispatcherOptions: React.FC<TmsDispatcherOptionsProps> = () => {
        const {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);

        return (
            <>
                <Form.Item label={t('central.personnel.pageDetails.hub')} name="warehouseId" rules={[{required: true, message: t('central.personnel.messages.pleaseSelectHub') as string}]}>
                    <Select
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder={t('central.personnel.placeholders.select') as string}
                    />
                </Form.Item>
            </>
        )
    };
    interface TmsDriverOptionsProps {
        form: FormInstance<AccountFormData>;
        mode: 'create' | 'edit'; // 添加 mode 参数
    }
    const TmsDriverOptions: React.FC<TmsDriverOptionsProps> = ({ form, mode }) => {
        const {t} = useTranslation();


        // 获取承运商下拉数据
        const { data: carrierData, loading: carrierLoading } = useQuery(GET_ALL_TMS_CARRIER_BY_WAREHOUSEID);
        const carrierOptions = (carrierData?.getAllTmsCarrierByWarehouseId || [])
            .map((c: any) => ({ label: c.carrierName, value: c.id, status: c.status }));

        // 监听 carrierId 的变化
        const carrierId = Form.useWatch('carrierId', form);

        // 根据选中的carrierId动态查询运输车辆信息
        const { data: vehicleData, loading: vehicleLoading, refetch: refetchVehicles } = useQuery<VehiclesResponse>(GET_VEHICLES, {
            variables: {
                carrierId: parseInt(carrierId+'') ,
                status: 1 // 只查询启用状态的车辆
            },
            skip:  !carrierId, // 当carrierId不存在时跳过查询
        });

        // 处理车辆选项数据
        const vehicleOptions = (vehicleData?.allVehicles || []).map((vehicle: TmsVehicleType) => {
            return {
                value: vehicle.id,
                label: vehicle.licensePlate ,
            };
        });

        // 当carrierId变化时，清空已选择的车辆
        const prevCarrierIdRef = useRef(carrierId);
        useEffect(() => {
            // 只有当carrierId真正发生变化时才清空车辆字段（不是初始化时）
            if (
                (prevCarrierIdRef.current !== carrierId && prevCarrierIdRef.current !== undefined)
            ) {
                form.setFieldsValue({
                    vehicleId: undefined
                });
            }
            prevCarrierIdRef.current = carrierId;
        }, [ carrierId, form]);

        return (
            <>
                <Form.Item
                    label={t('tms.vehicle.pageDetails.carrier')}
                    name="carrierId"
                    rules={[{required: true, message: t('tms.vehicle.messages.pleaseSelectCarrier') as string}]}
                >
                    <Select
                        loading={carrierLoading}
                        options={carrierOptions}
                        placeholder={t('central.personnel.placeholders.select') as string}
                        disabled={mode === 'edit'} // 编辑模式下禁用选择
                    />
                </Form.Item>

                <Form.Item
                    label={t('central.personnel.pageDetails.vehicle')}
                    name="vehicleId"
                >
                    <Select
                        loading={vehicleLoading}
                        placeholder={
                                 (t('central.personnel.placeholders.pleaseSelectVehicle') as string)
                        }
                        disabled={ !carrierId }
                        allowClear
                        options={vehicleOptions}
                    />
                </Form.Item>
            </>
        )
    };
    return (
        <Form
            form={form}
            onFinish={onFinish}
            labelCol={{span: 6}}
            wrapperCol={{span: 18}}
        >
            {mode === 'edit' && (
                <Form.Item label="id" name="id">
                    <Input placeholder={t('central.personnel.pageDetails.inputPlaceholder') as string} disabled/>
                </Form.Item>
            )}
            <Form.Item label={t('central.personnel.pageDetails.phone')}
                       name="phone"
                       rules={[
                           {required: true, message: t('central.personnel.messages.pleaseInputPhoneNumber') as string},
                           {
                               validator: (_, value: CountryCodeAndPhoneNumber) => {
                                   if (!value || !value.phoneNumber) {
                                       return Promise.reject(new Error(t('central.personnel.messages.phoneNumberRequired') as string));
                                   }

                                   // 验证手机号只包含数字
                                   const phoneRegex = /^\d+$/;
                                   if (!phoneRegex.test(value.phoneNumber)) {
                                       return Promise.reject(new Error(t('central.personnel.messages.phoneNumberDigitsOnly') as string));
                                   }

                                   return Promise.resolve();
                               }
                           }
                       ]}
            >
                <PhoneInput/>
            </Form.Item>
            <Form.Item
                label={t('central.personnel.pageDetails.password')}
                name="password"
                validateTrigger="onBlur"
                rules={mode === 'create'
                    ? [{required: true, message: t('central.personnel.messages.pleaseInputPassword') as string}, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
                    : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]}
            >
                <Input.Password placeholder={t('accountSecurity.passwordHint') as string}/>
            </Form.Item>
            <Form.Item
                label={t('central.personnel.pageDetails.name')}
                name="name"
                rules={[{required: true, message: t('central.personnel.messages.pleaseInputName') as string}]}
            >
                <Input placeholder={t('central.personnel.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item
                label={t('central.personnel.pageDetails.email')}
                name="email"
                rules={[{type: 'email', message: t('central.personnel.messages.pleaseInputValidEmail') as string}]}
            >
                <Input placeholder={t('central.personnel.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item label={t('central.personnel.pageDetails.status')} name="status">
                <Select
                    options={statusOptions}
                    placeholder={t('central.personnel.placeholders.select') as string}
                />
            </Form.Item>
            <Form.Item
                label={t('central.personnel.pageDetails.role')}
                name="role"
                rules={[{required: true, message: t('central.personnel.messages.pleaseSelectRole') as string}]}
            >
                <Select
                    disabled={mode === 'edit'}
                    options={getRoleFormOptions()}
                    placeholder={t('components.input.select') as string}
                />
            </Form.Item>
            {
                role === 'DSP_MANAGER' && <DspManagerOptions/>
            }{
                role === 'DSP_OWNER_TEAM' && <DspManagerOptions/>
            }
            {
                role === 'HUB_MANAGER' && <HubManagerOptions/>
            }
            {
                role === 'WAREHOUSE_MANAGER' && <WarehouseManagerOptions/>
            }
            {
                (role === 'WAREHOUSE_OPERATOR' || role === 'WAREHOUSE_TAG_SORTER') && <WarehouseOperatorOptions/>
            }
            {
                (role === 'HUB_OPERATOR' || role === 'SORTING_STAFF' || role === 'SORTER_OPERATOR' || role === 'RETURNS_STAFF' || role === 'PICKUP_STAFF') && <HubOperatorOptions/>
            }
            {
                role === 'PICKUP_DISPATCHER' && <PickupDispatcherOptions/>
            }
            {
                role === 'PICKUP_DRIVER' && <PickupDriverOptions/>
            }
            {
                role === 'TMS_DISPATCHER' && <TmsDispatcherOptions form={form} mode={mode}/>
            }
            {
                role === 'TMS_DRIVER' && <TmsDriverOptions form={form} mode={mode}/>
            }

        </Form>
    )
};

export default AccountFormDrawer; 